import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AreasProvider } from './contexts/AreasContext';
import { ModelsProvider } from './contexts/ModelsContext';
import Loader from './components/Loader/Loader';
import AppLayout from './components/AppLayout/AppLayout';
import './App.css';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Area from './pages/Area/Area';
import Profile from './pages/Profile/Profile';
import Balance from './pages/Balance/Balance';
import Admin from './pages/Admin/Admin';
import AreaSubscriptionGuard from './components/AreaSubscriptionGuard/AreaSubscriptionGuard';
import ModelCategory from './pages/ModelCategory/ModelCategory';
import MetrikaPageViews from './components/MetrikaPageViews/MetrikaPageViews';
import Subaccounts from './pages/Subaccounts/Subaccounts';
import Guide from './pages/Guide/Guide';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loader fullScreen size="large" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <Loader fullScreen size="large" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

const MainAccountRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user?.is_subaccount ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <MetrikaPageViews />
      <div className="App">
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area/:id"
            element={
              <ProtectedRoute>
                <AreaSubscriptionGuard>
                  <Area />
                </AreaSubscriptionGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route path="/plates" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/model-category/:categoryId"
            element={
              <ProtectedRoute>
                <ModelCategory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/balance"
            element={
              <ProtectedRoute>
                <MainAccountRoute><Balance /></MainAccountRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/subaccounts" element={<ProtectedRoute><MainAccountRoute><Subaccounts /></MainAccountRoute></ProtectedRoute>} />
          <Route path="/guide" element={<ProtectedRoute><Guide /></ProtectedRoute>} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AreasProvider>
          <ModelsProvider>
            <SubscriptionProvider>
              <AppRoutes />
            </SubscriptionProvider>
          </ModelsProvider>
        </AreasProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
